import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { StatusChip } from "@/components/common/StatusChip";
import { VoiceScriptPlayer } from "@/components/common/VoiceScriptPlayer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const TemplatesTab = () => {
  const { t } = useT();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [cuerpo, setCuerpo] = useState("");
  const [asunto, setAsunto] = useState("");
  // Fix O (2026-09-29): ContentSid de la plantilla aprobada en Twilio (solo WhatsApp).
  const [contentSid, setContentSid] = useState("");
  const [woId, setWoId] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ["message-templates"],
    queryFn: async () => (await api.get("/message-templates")).data,
  });
  const { data: wos } = useQuery({
    queryKey: ["templates-wos"],
    queryFn: async () => (await api.get("/work-orders", { params: { page_size: 12 } })).data,
  });

  const items = data?.items || [];
  const selected = useMemo(
    () => items.find((x) => x.id === selectedId) || items[0],
    [items, selectedId]
  );

  useEffect(() => {
    if (selected) {
      setCuerpo(selected.cuerpo || "");
      setAsunto(selected.asunto || "");
    }
  }, [selected?.id]);

  useEffect(() => {
    setContentSid(selected?.twilio_content_sid || "");
  }, [selected?.id, selected?.twilio_content_sid]);

  useEffect(() => {
    if (!cuerpo) return;
    const timer = setTimeout(async () => {
      try {
        const { data: res } = await api.post("/message-templates/preview", {
          cuerpo, asunto, work_order_id: woId || undefined,
        });
        setPreview(res);
      } catch (e) {
        /* la vista previa no bloquea la edición */
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [cuerpo, asunto, woId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/message-templates/${selected.id}`, {
        cuerpo, asunto: asunto || null,
        ...(selected.canal === "whatsapp" ? { twilio_content_sid: contentSid.trim() } : {}),
      });
      qc.invalidateQueries({ queryKey: ["message-templates"] });
      toast.success(t("app.saved"));
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  const grouped = items.reduce((acc, x) => {
    (acc[x.evento] = acc[x.evento] || []).push(x);
    return acc;
  }, {});

  return (
    <div className="grid gap-4 xl:grid-cols-[300px_1fr_300px]" data-testid="templates-tab">
      <section className="max-h-[70vh] overflow-y-auto rounded-[8px] border border-line bg-white">
        <header className="sticky top-0 border-b border-line bg-white px-3 py-2.5">
          <h2 className="text-[13px] font-semibold text-ink">{t("templates.list")}</h2>
        </header>
        <ul>
          {Object.entries(grouped).map(([evento, group]) => (
            <li key={evento} className="border-b border-line px-3 py-2 last:border-0">
              <p className="text-[12px] font-medium text-ink">{t(`messages.events.${evento}`)}</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {group.map((x) => (
                  <button key={x.id} data-testid={`template-pick-${x.evento}-${x.canal}-${x.idioma}`}
                    onClick={() => setSelectedId(x.id)}
                    className={`rounded-[6px] px-2 py-[3px] text-[11.5px] transition-colors ${
                      selected?.id === x.id
                        ? "bg-ink text-white"
                        : "bg-surface-sunken text-ink-secondary hover:bg-line"
                    }`}>
                    {t(`channels.${x.canal}`)} · {x.idioma}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-[8px] border border-line bg-white p-4">
        {!selected ? (
          <p className="text-[13px] text-ink-tertiary">{t("app.loading")}</p>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <StatusChip tone={selected.canal === "voz" ? "amber" : "blue"} label={t(`channels.${selected.canal}`)} />
              <StatusChip tone="gray" label={selected.idioma === "es" ? "Español" : "English"} />
              <span className="text-[13px] font-semibold text-ink">{t(`messages.events.${selected.evento}`)}</span>
            </div>

            {selected.canal === "email" && (
              <div className="mb-3">
                <Label className="text-[12px] text-ink-secondary">{t("templates.subject")}</Label>
                <Input data-testid="template-subject" value={asunto} onChange={(e) => setAsunto(e.target.value)}
                  className="mt-1 h-9 rounded-[6px] text-[13.5px]" />
              </div>
            )}

            <Label className="text-[12px] text-ink-secondary">{t("templates.body")}</Label>
            <Textarea data-testid="template-body" value={cuerpo} rows={10}
              onChange={(e) => setCuerpo(e.target.value)}
              className="mt-1 rounded-[6px] font-mono text-[12.5px] leading-relaxed" />

            <div className="mt-2 flex flex-wrap gap-1.5">
              {(data?.variables || []).map((v) => (
                <button key={v} data-testid={`template-var-${v}`}
                  onClick={() => setCuerpo((c) => `${c}{{${v}}}`)}
                  className="rounded-[5px] bg-surface-sunken px-1.5 py-[2px] font-mono text-[11px] text-ink-secondary hover:bg-line">
                  {`{{${v}}}`}
                </button>
              ))}
            </div>

            <div className="mt-3 flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-[12px] text-ink-secondary">{t("templates.preview_with")}</Label>
                <Select value={woId || "sample"} onValueChange={(v) => setWoId(v === "sample" ? "" : v)}>
                  <SelectTrigger data-testid="template-wo-select" className="mt-1 h-8 w-[260px] rounded-[6px] text-[12.5px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sample">{t("templates.sample_data")}</SelectItem>
                    {(wos?.items || []).map((w) => (
                      <SelectItem key={w.id} value={w.id}>{w.code} · {w.property_nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {can("messaging", "manage") && (
                <Button data-testid="template-save" disabled={busy} onClick={save}
                  className="h-8 rounded-[6px] bg-brand text-[12.5px] hover:bg-brand-hover">
                  {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
                  {t("app.save")}
                </Button>
              )}
            </div>

            {selected.whatsapp && (
              <div className="mt-4 rounded-[8px] border border-line bg-surface-sunken p-3" data-testid="template-whatsapp-struct">
                <p className="text-[12px] font-semibold text-ink">{t("templates.wa_struct")}</p>
                <p className="mt-1 text-[11.5px] text-ink-secondary">
                  {selected.whatsapp.nombre} · {selected.whatsapp.categoria} · {selected.whatsapp.idioma}
                </p>
                <pre className="mt-1.5 overflow-x-auto whitespace-pre-wrap rounded-[6px] bg-white p-2 font-mono text-[11px] text-ink-secondary">
{selected.whatsapp.cuerpo_numerado}
                </pre>
                <Label className="mt-3 block text-[12px] text-ink-secondary">{t("templates.content_sid")}</Label>
                <Input data-testid="template-content-sid" value={contentSid} placeholder="HX…"
                  disabled={!can("messaging", "manage")}
                  onChange={(e) => setContentSid(e.target.value)}
                  className="mt-1 h-8 rounded-[6px] bg-white font-mono text-[12px]" />
                <p className="mt-1 text-[11px] text-ink-tertiary">{t("templates.content_sid_help")}</p>
              </div>
            )}
          </>
        )}
      </section>

      <section className="rounded-[8px] border border-line bg-white p-4" data-testid="template-preview">
        <p className="mb-3 flex items-center gap-1.5 text-[13px] font-semibold text-ink">
          <Smartphone className="h-3.5 w-3.5" /> {t("templates.live_preview")}
        </p>
        <div className="mx-auto w-[248px] rounded-[26px] border-[7px] border-ink bg-[var(--wa-bg)] p-2.5 shadow-[0_10px_30px_var(--ink-veil)]">
          <div className="mb-2 flex items-center justify-between rounded-[8px] bg-[var(--wa-header)] px-2 py-1 text-[10px] text-white">
            <span>Beto's Handyman</span>
            <span>{selected ? t(`channels.${selected.canal}`) : ""}</span>
          </div>
          <div className="min-h-[240px]">
            <div className="ml-auto max-w-[92%] rounded-[8px] rounded-tr-[2px] bg-[var(--wa-bubble)] px-2.5 py-2">
              {preview?.asunto && (
                <p className="mb-1 text-[11px] font-semibold text-[var(--wa-header)]">{preview.asunto}</p>
              )}
              <p data-testid="template-preview-body" className="whitespace-pre-wrap text-[11.5px] leading-snug text-ink">
                {preview?.cuerpo || "—"}
              </p>
              <p className="mt-1 text-right text-[9.5px] text-ink-secondary">12:04 ✓✓</p>
            </div>
          </div>
        </div>
        {selected?.canal === "voz" && preview?.cuerpo && (
          <div className="mt-3 flex justify-center">
            <VoiceScriptPlayer script={preview.cuerpo} label={t("messages.play_script")} testId="template-preview-play" />
          </div>
        )}
      </section>
    </div>
  );
};
